import { LoginRequiredError, useAuth } from "@workos-inc/authkit-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { isNetworkFetchFailure } from "@/lib/network-errors";
import { breadcrumb, captureError } from "@/lib/observability";

/**
 * Token cache to prevent unnecessary WorkOS SDK calls during refresh.
 * This smooths out token refresh so it happens in the background without
 * causing React state updates that could trigger page re-renders.
 */
interface TokenCache {
  token: string | null;
  expiresAt: number; // Unix timestamp in ms
  refreshPromise: Promise<string | null> | null;
}

export type WorkOSConvexTokenStatus = "idle" | "loading" | "ready" | "unavailable";

export interface WorkOSConvexTokenStatusSnapshot {
  status: WorkOSConvexTokenStatus;
  reason?: string;
  updatedAt: number;
}

let tokenStatusSnapshot: WorkOSConvexTokenStatusSnapshot = {
  status: "idle",
  updatedAt: Date.now(),
};

// Token refresh buffer: refresh 1 minute before expiry to ensure smooth handover
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000; // 1 minute

// WorkOS access token lifetime (5 minutes)
const TOKEN_LIFETIME_MS = 5 * 60 * 1000;

// How often to check if token needs proactive refresh
const TOKEN_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds
const TOKEN_FAILURE_CAPTURE_INTERVAL_MS = 5 * 60 * 1000;
// About a minute of proactive refreshes; a single failure after wake or reconnect is routine.
const UNREACHABLE_STREAK_THRESHOLD = 3;

type TokenRefreshFailureClass = "session_ended" | "unreachable" | "unexpected";

let lastTokenFailureCaptureAt = 0;
let sessionEndedReported = false;
let unreachableStreak = 0;

/** Test-only: reset the tab-wide failure reporting state. */
export function resetTokenRefreshTrackingForTest() {
  lastTokenFailureCaptureAt = 0;
  resetTokenRefreshFailureState();
}

function resetTokenRefreshFailureState() {
  sessionEndedReported = false;
  unreachableStreak = 0;
}

function classifyTokenRefreshFailure(error: unknown): TokenRefreshFailureClass {
  // AuthKit throws this once WorkOS rejects the refresh token (session ended, token already
  // exchanged) and on every later call without another request.
  if (error instanceof LoginRequiredError) return "session_ended";
  if (isNetworkFetchFailure(error)) return "unreachable";
  // AuthKit's refresh-lock wait timed out: `AbortSignal.timeout` rejects with a `TimeoutError`
  // DOMException (Safari's is not always `instanceof Error`), the lock fallback with its own name.
  const name = typeof error === "object" && error !== null && "name" in error ? error.name : null;
  if (name === "TimeoutError" || name === "AcquisitionTimeoutError") return "unreachable";
  return "unexpected";
}

function captureTokenRefreshFailure(error: unknown, reason: string) {
  const failureClass = classifyTokenRefreshFailure(error);

  if (failureClass === "session_ended") {
    // shortcut: AuthKit also reports a WorkOS 5xx with a JSON body as LoginRequiredError, so an
    // outage shows only as this group escalating; split it if AuthKit ever exposes the status.
    if (sessionEndedReported) return;
    sessionEndedReported = true;
    captureError(error, {
      feature: "auth",
      operation: "token-refresh",
      level: "warning",
      fingerprint: ["auth-token-refresh-session-ended"],
      extra: { reason, failureClass, outcome: "token_unavailable" },
    });
    return;
  }

  if (failureClass === "unreachable") {
    if (globalThis.navigator?.onLine === false) return;
    unreachableStreak += 1;
    if (unreachableStreak !== UNREACHABLE_STREAK_THRESHOLD) return;
    captureError(error, {
      feature: "auth",
      operation: "token-refresh",
      level: "warning",
      fingerprint: ["auth-token-refresh-unreachable"],
      extra: {
        reason,
        failureClass,
        consecutiveFailures: unreachableStreak,
        outcome: "token_unavailable",
      },
    });
    return;
  }

  const now = Date.now();
  if (now - lastTokenFailureCaptureAt < TOKEN_FAILURE_CAPTURE_INTERVAL_MS) {
    return;
  }
  lastTokenFailureCaptureAt = now;

  captureError(error instanceof Error ? error : new Error(`Token refresh failed: ${reason}`), {
    feature: "auth",
    operation: "token-refresh",
    extra: { reason, outcome: "token_unavailable" },
  });
}

function isTokenCacheValid(
  cache: TokenCache,
  now: number
): cache is TokenCache & { token: string } {
  return Boolean(cache.token) && cache.expiresAt > now;
}

function getAuthUserCacheKey(user: unknown): string | null {
  if (!user || typeof user !== "object") return null;
  const id = (user as { id?: unknown }).id;
  return typeof id === "string" && id.length > 0 ? id : "authenticated";
}

export function getWorkOSConvexTokenStatusSnapshot(): WorkOSConvexTokenStatusSnapshot {
  return tokenStatusSnapshot;
}

/**
 * Custom hook that bridges WorkOS AuthKit to Convex auth.
 *
 * This implementation includes several optimizations to prevent the automatic
 * page refreshes that can occur during token renewal:
 *
 * 1. **Token caching**: Caches the token and its expiry time to avoid calling
 *    getAccessToken() on every Convex request.
 *
 * 2. **Proactive refresh**: Refreshes the token 1 minute before expiry so that
 *    a fresh token is always available when Convex needs it.
 *
 * 3. **Stable callback reference**: Uses refs to keep the fetchAccessToken
 *    callback stable, preventing unnecessary re-renders in ConvexProviderWithAuth.
 *
 * 4. **Readiness state**: Token refresh updates React state only when Convex
 *    auth readiness changes, so protected subscriptions stay gated while token
 *    acquisition is unavailable.
 *
 * @returns Auth state compatible with ConvexProviderWithAuth's useAuth prop
 *
 * @example
 * ```tsx
 * <ConvexProviderWithAuth client={convex} useAuth={useAuthFromWorkOS}>
 *   <App />
 * </ConvexProviderWithAuth>
 * ```
 */
export function useAuthFromWorkOS() {
  const { user, getAccessToken, isLoading } = useAuth();
  const [tokenStatus, setTokenStatusState] = useState<WorkOSConvexTokenStatus>("idle");

  // Use refs to store the latest getAccessToken function and user state
  // This prevents the fetchAccessToken callback from changing reference
  const getAccessTokenRef = useRef(getAccessToken);
  const userRef = useRef(user);
  const userCacheKeyRef = useRef<string | null>(null);
  const hasReadyTokenRef = useRef(false);
  const tokenCacheRef = useRef<TokenCache>({
    token: null,
    expiresAt: 0,
    refreshPromise: null,
  });

  const setTokenStatus = useCallback((status: WorkOSConvexTokenStatus, reason?: string) => {
    tokenStatusSnapshot = {
      status,
      reason,
      updatedAt: Date.now(),
    };
    if (status === "ready") {
      hasReadyTokenRef.current = true;
    } else if (status === "idle") {
      hasReadyTokenRef.current = false;
    }
    setTokenStatusState(status);
  }, []);

  const clearTokenCache = useCallback(() => {
    tokenCacheRef.current = {
      token: null,
      expiresAt: 0,
      refreshPromise: null,
    };
    hasReadyTokenRef.current = false;
    resetTokenRefreshFailureState();
  }, []);

  const refreshAccessToken = useCallback(
    async (options?: { forceRefreshToken?: boolean; reason?: string; markLoading?: boolean }) => {
      if (!userRef.current) {
        clearTokenCache();
        setTokenStatus("idle", "no-user");
        return null;
      }

      const cache = tokenCacheRef.current;
      const now = Date.now();

      if (!options?.forceRefreshToken && isTokenCacheValid(cache, now)) {
        setTokenStatus("ready", "cached-token");
        return cache.token;
      }

      if (cache.refreshPromise) {
        return cache.refreshPromise;
      }

      if (options?.markLoading) {
        setTokenStatus("loading", options.reason ?? "token-refresh");
      }

      const previousToken = cache.token;
      const previousExpiresAt = cache.expiresAt;

      const refreshPromise = (async () => {
        try {
          const token = await getAccessTokenRef.current();

          if (typeof token === "string" && token.length > 0) {
            tokenCacheRef.current = {
              token,
              expiresAt: Date.now() + TOKEN_LIFETIME_MS,
              refreshPromise: null,
            };
            resetTokenRefreshFailureState();
            setTokenStatus("ready", options?.reason ?? "token-refresh-success");
            return token;
          }

          const fallbackCache: TokenCache = {
            token: previousToken,
            expiresAt: previousExpiresAt,
            refreshPromise: null,
          };
          tokenCacheRef.current = fallbackCache;

          breadcrumb(
            "auth.token-unavailable",
            { reason: options?.reason ?? "token-null" },
            { category: "network", level: "warning" }
          );

          if (isTokenCacheValid(fallbackCache, Date.now())) {
            setTokenStatus("ready", "token-null-using-cached-token");
            return fallbackCache.token;
          }

          const reason = options?.reason ?? "token-null";
          captureTokenRefreshFailure(new Error("Token refresh returned no token"), reason);
          setTokenStatus("unavailable", reason);
          return null;
        } catch (error) {
          const fallbackCache: TokenCache = {
            token: previousToken,
            expiresAt: previousExpiresAt,
            refreshPromise: null,
          };
          tokenCacheRef.current = fallbackCache;

          breadcrumb(
            "auth.token-unavailable",
            {
              reason: options?.reason ?? "token-fetch-error",
              error: error instanceof Error ? error.message : "Unknown",
              failureClass: classifyTokenRefreshFailure(error),
            },
            { category: "network", level: "warning" }
          );

          if (isTokenCacheValid(fallbackCache, Date.now())) {
            setTokenStatus("ready", "token-error-using-cached-token");
            return fallbackCache.token;
          }

          const reason = options?.reason ?? "token-fetch-error";
          captureTokenRefreshFailure(error, reason);
          setTokenStatus("unavailable", reason);
          return null;
        }
      })();

      tokenCacheRef.current = {
        token: previousToken,
        expiresAt: previousExpiresAt,
        refreshPromise,
      };
      return refreshPromise;
    },
    [clearTokenCache, setTokenStatus]
  );

  // Keep refs up to date
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
    userRef.current = user;

    const userCacheKey = getAuthUserCacheKey(user);
    if (userCacheKeyRef.current !== userCacheKey) {
      clearTokenCache();
      userCacheKeyRef.current = userCacheKey;
    }
  }, [clearTokenCache, getAccessToken, user]);

  // Initial token fetch: AuthKit user presence is not enough for Convex auth.
  useEffect(() => {
    if (isLoading) {
      setTokenStatus("loading", "authkit-loading");
      return;
    }

    if (!user) {
      clearTokenCache();
      setTokenStatus("idle", "no-user");
      return;
    }

    if (isTokenCacheValid(tokenCacheRef.current, Date.now())) {
      setTokenStatus("ready", "cached-token");
      return;
    }

    void refreshAccessToken({
      forceRefreshToken: true,
      reason: "initial-token-fetch",
      markLoading: true,
    });
  }, [clearTokenCache, isLoading, refreshAccessToken, setTokenStatus, user]);

  // Proactive token refresh: refresh before expiry after the initial token exists.
  useEffect(() => {
    if (!user) return;

    // Set up proactive refresh interval (refresh 1 minute before expiry)
    // Using 4 minutes = 240 seconds to refresh before the 5-minute expiry
    const refreshInterval = setInterval(() => {
      const cache = tokenCacheRef.current;
      const now = Date.now();
      const shouldRefresh = cache.expiresAt - now < TOKEN_REFRESH_BUFFER_MS;

      if (shouldRefresh && !cache.refreshPromise) {
        void refreshAccessToken({
          forceRefreshToken: true,
          reason: "proactive-refresh",
        });
      }
    }, TOKEN_CHECK_INTERVAL_MS);

    return () => clearInterval(refreshInterval);
  }, [refreshAccessToken, user]);

  // Stable callback that doesn't change reference
  // Uses refs internally to access latest state
  const fetchAccessToken = useCallback(
    async (options: { forceRefreshToken: boolean }) => {
      return refreshAccessToken({
        forceRefreshToken: options.forceRefreshToken,
        reason: options.forceRefreshToken ? "convex-forced-refresh" : "convex-token-fetch",
        markLoading: options.forceRefreshToken,
      });
    },
    [refreshAccessToken]
  );

  const hasValidToken = isTokenCacheValid(tokenCacheRef.current, Date.now());
  const hasEstablishedConvexSession = hasReadyTokenRef.current;
  // Forced refreshes for established sessions should not blank the whole app shell.
  const isEstablishedSessionRefreshing =
    Boolean(user) && hasEstablishedConvexSession && tokenStatus === "loading";
  const isTokenLoading =
    Boolean(user) &&
    !hasEstablishedConvexSession &&
    (tokenStatus === "idle" || tokenStatus === "loading");

  return {
    isLoading: isLoading || isTokenLoading,
    isAuthenticated:
      Boolean(user) &&
      ((tokenStatus === "ready" && hasValidToken) || isEstablishedSessionRefreshing),
    fetchAccessToken,
  };
}
